import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetLmsTenderRequestByIdQuery, useGetLmsLookupTranslationsQuery, useCalculateLmsTenderRequestEndDateMutation } from '../../../../api';
import LmsCodeDropdown from '../../components/LmsCodeDropdown';
import useLmsLanguageOptions from '../../hooks/useLmsLanguageOptions';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import { TENDER_STATUS, TENDER_STATUS_ACTIONS } from '../utils/tenderRequestStatus';

// bkz. docs/LMS-IHALE--FE-Dokumani.txt §5. "Müşteri Kendi Adresinde" kodu — seçilince Açık Adres
// alanı açılıyor.
const CUSTOM_ADDRESS_LOCATION_CODE = '4';

// Onayla/İade/Reddet görünümü — bkz. warranty-claims/createEditForm/component/FormWarrantyClaims.jsx
// (aynı paylaşılan src/components/ButtonGroup bileşeni, aynı renk/ikon eşlemesi: onayla=success
// (mavi), iade=gray (gri, sol ok), reddet=danger (kırmızı)) — kullanıcı isteği üzerine oradan
// birebir örnek alındı.
const TENDER_ACTION_BUTTON_OVERRIDES = {
    [TENDER_STATUS.PENDING_APPROVAL]: { severity: 'success', icon: 'pi pi-send' },
    [TENDER_STATUS.ACCEPTED]: { severity: 'success', icon: 'pi pi-check' },
    [TENDER_STATUS.RETURNED]: { severity: 'gray', icon: 'pi pi-arrow-left' },
    [TENDER_STATUS.REJECTED]: { severity: 'danger', icon: 'pi pi-times' }
};

const toRequestDateTime = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T09:00:00`;
};

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

// Ihale ekraninda tercih edilen / alternatif tarih bugunden en az 14 gun sonrasi olmali.
// Bu kural sadece ihalede gecerli - bkz. docs/LMS-FE-API-Dokumani-Ihale-Date-Tercuman.txt SS3.1.
const MIN_TENDER_DATE_OFFSET_DAYS = 14;

const minTenderTrainingDate = () => {
    const d = startOfToday();
    d.setDate(d.getDate() + MIN_TENDER_DATE_OFFSET_DAYS);
    return d;
};

const formatDisplayDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}`;

const emptyLocation = () => ({ id: null, trainingLocation: null, fullAddress: '' });
const emptyResponsibleRow = () => ({ rowKey: nextRowKey(), id: null, firstName: '', lastName: '', phoneNumber: '', eMail: '' });
const emptyTechnicianRow = () => ({ rowKey: nextRowKey(), id: null, firstName: '', lastName: '', duty: null });

const emptyFormData = () => ({
    id: null,
    vehicleModel: null,
    tenderRequestType: null,
    location: emptyLocation(),
    responsibles: [],
    numberOfTechnician: null,
    technicians: [],
    numberOfDrivers: null,
    trainingDayCount: null,
    preferTrainingDate: null,
    alternativeDate: null,
    // Backend hesapliyor, formda sadece anlik gosterim icin tutuluyor - payload'a girmiyor.
    trainingEndDate: null,
    alternativeEndDate: null,
    description: '',
    trainingLanguage: '',
    translationSupport: 2,
    translatorName: '',
    translatorEmail: '',
    translatorPhone: ''
});

/**
 * İhale Talebi ekle/düzenle diyaloğu — bkz. docs/LMS-IHALE--FE-Dokumani.txt §1 ve §5 (alan
 * eşleştirme). Desen `training-request/components/AllRequestsForm.jsx`'teki canEdit/canDelete
 * salt-okunur mantığıyla aynı; alt listeler (locations/responsibles/technicians) id bazlı
 * eşleştirme kuralına göre gönderiliyor (bkz. doküman §6) — GetById'den gelen `id` korunuyor,
 * yeni satırlarda `id` gönderilmiyor.
 */
const TenderRequestForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, onStatusAction, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const isEdit = !!(initialData && (initialData.requestId || initialData.id));
    const editId = isEdit ? initialData.requestId ?? initialData.id : null;
    const { data: detailData, isLoading: isLoadingDetail } = useGetLmsTenderRequestByIdQuery(editId, { skip: !isEdit || !visible });

    const sourceData = isEdit ? detailData || initialData : null;

    const rawCanEdit = sourceData?.canEdit ?? initialData?.canEdit;
    const canEditRecord = !readOnly && (!isEdit || rawCanEdit !== false);
    const rawCanDelete = sourceData?.canDelete ?? initialData?.canDelete;
    const canDeleteRecord = isEdit && !!handleDelete && rawCanDelete === true;

    // Onayla/Red/İade — bkz. TenderRequestTable.jsx'teki actionBodyTemplate ile aynı desen
    // (aynı ikon/renk seti), dialog içinden de aynı statü değişikliği akışını (onay yorumu vb.)
    // tetiklemek için index.jsx'teki handleStatusAction'a delege ediyor.
    const allowedTenderActions = useMemo(() => {
        const allowed = sourceData?.allowedStatuses;
        return Array.isArray(allowed) ? TENDER_STATUS_ACTIONS.filter((action) => allowed.includes(action.targetStatus)) : [];
    }, [sourceData]);

    const { languageOptions, isLoadingLanguages } = useLmsLanguageOptions();

    const { data: technicianDutiesData, isLoading: isLoadingDuties } = useGetLmsLookupTranslationsQuery({ groupCode: 'TECHNICIAN_DUTY', isActive: 1, pageNumber: 1, pageSize: 100 });
    const dutyOptions = useMemo(() => (technicianDutiesData?.items || []).map((item) => ({ value: item.code, label: item.text })), [technicianDutiesData]);

    const [formData, setFormData] = useState(emptyFormData());
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (!visible) return;
        if (isEdit && !sourceData) return;

        if (!sourceData) {
            setFormData(emptyFormData());
            setLocalSubmitted(false);
            return;
        }

        setFormData({
            id: sourceData.requestId ?? sourceData.id ?? null,
            vehicleModel: sourceData.vehicleModel ?? null,
            tenderRequestType: sourceData.tenderRequestType ?? null,
            // Lokasyon artık tek seçim — bkz. bu dosyanın üstündeki not. BE dizisinden yalnızca
            // ilk kayıt alınır (id korunur, id-bazlı reconciliation kuralı gereği kaydedilince
            // dizideki fazla kayıtlar zaten soft-delete edilir).
            location: sourceData.locations && sourceData.locations.length > 0 ? { id: sourceData.locations[0].id ?? null, trainingLocation: sourceData.locations[0].trainingLocation ?? null, fullAddress: sourceData.locations[0].fullAddress ?? '' } : emptyLocation(),
            responsibles: (sourceData.responsibles || []).map((r) => ({ rowKey: nextRowKey(), id: r.id ?? null, firstName: r.firstName ?? '', lastName: r.lastName ?? '', phoneNumber: r.phoneNumber ?? '', eMail: r.eMail ?? '' })),
            numberOfTechnician: sourceData.numberOfTechnician ?? null,
            technicians: (sourceData.technicians || []).map((t) => ({ rowKey: nextRowKey(), id: t.id ?? null, firstName: t.firstName ?? '', lastName: t.lastName ?? '', duty: t.duty ?? null })),
            numberOfDrivers: sourceData.numberOfDrivers ?? null,
            trainingDayCount: sourceData.trainingDayCount ?? null,
            preferTrainingDate: sourceData.preferTrainingDate ? new Date(sourceData.preferTrainingDate) : null,
            alternativeDate: sourceData.alternativeDate ? new Date(sourceData.alternativeDate) : null,
            trainingEndDate: sourceData.trainingEndDate ? new Date(sourceData.trainingEndDate) : null,
            alternativeEndDate: sourceData.alternativeEndDate ? new Date(sourceData.alternativeEndDate) : null,
            description: sourceData.description ?? '',
            trainingLanguage: sourceData.trainingLanguage ?? '',
            translationSupport: sourceData.translationSupport ?? 2,
            translatorName: sourceData.translatorName ?? '',
            translatorEmail: sourceData.translatorEmail ?? '',
            translatorPhone: sourceData.translatorPhone ?? ''
        });
        setLocalSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isEdit, sourceData]);

    const [calculateEndDate, { isLoading: isCalculatingEndDate }] = useCalculateLmsTenderRequestEndDateMutation();

    // Formda anlik bitis tarihi onizlemesi - bkz. docs/LMS-FE-API-Dokumani-Ihale-Date-Tercuman.txt SS3.2.
    // Kayit olusturmaz, sadece gosterir; 14 gunluk kural burada uygulanmaz.
    useEffect(() => {
        if (!visible || !canEditRecord || isLoadingDetail) return;

        const timer = setTimeout(() => {
            if (formData.trainingDayCount == null || formData.trainingDayCount === '') {
                setFormData((prev) => ({ ...prev, trainingEndDate: null, alternativeEndDate: null }));
                return;
            }

            calculateEndDate({
                preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
                alternativeDate: toRequestDateTime(formData.alternativeDate),
                trainingDayCount: Number(formData.trainingDayCount)
            })
                .unwrap()
                .then((result) => {
                    setFormData((prev) => ({
                        ...prev,
                        trainingEndDate: result?.trainingEndDate ? new Date(result.trainingEndDate) : null,
                        alternativeEndDate: result?.alternativeEndDate ? new Date(result.alternativeEndDate) : null
                    }));
                })
                .catch(() => {
                    // Anlik onizleme - hata olursa sessizce eski degerleri koru, kaydi engellemez.
                });
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, canEditRecord, isLoadingDetail, formData.preferTrainingDate, formData.alternativeDate, formData.trainingDayCount]);

    // Talep tipine göre alan gösterimi — bkz. TENDER_REQUEST_TYPE kodları: 1 Sürücü Eğitimi
    // (Şoför sayısı), 2 Teknisyen Eğitimi (Teknisyen sayısı + listesi), 3 Full Eğitim (ikisi birden).
    const tenderRequestTypeNum = formData.tenderRequestType != null ? Number(formData.tenderRequestType) : null;
    const showDriverCount = tenderRequestTypeNum === 1 || tenderRequestTypeNum === 3;
    const showTechnicianFields = tenderRequestTypeNum === 2 || tenderRequestTypeNum === 3;

    const checkFieldInvalid = (field) => {
        if (field === 'numberOfTechnician') return formData.numberOfTechnician != null && !(Number(formData.numberOfTechnician) > 0);
        if (field === 'numberOfDrivers') return formData.numberOfDrivers != null && !(Number(formData.numberOfDrivers) > 0);
        if (field === 'preferTrainingDate') return !!formData.preferTrainingDate && formData.preferTrainingDate < minTenderTrainingDate();
        if (field === 'alternativeDate') return !!formData.alternativeDate && formData.alternativeDate < minTenderTrainingDate();
        if (typeof formData[field] === 'string') return !formData[field].trim();
        return formData[field] == null;
    };

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        return checkFieldInvalid(field);
    };

    const updateRow = (listKey, rowKey, patch) => setFormData((prev) => ({ ...prev, [listKey]: prev[listKey].map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)) }));
    const addRow = (listKey, factory) => setFormData((prev) => ({ ...prev, [listKey]: [...prev[listKey], factory()] }));
    const removeRow = (listKey, rowKey) => setFormData((prev) => ({ ...prev, [listKey]: prev[listKey].filter((row) => row.rowKey !== rowKey) }));

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const nonEmptyResponsibles = formData.responsibles.filter((row) => row.firstName.trim() || row.lastName.trim() || row.phoneNumber.trim() || row.eMail.trim());
        const nonEmptyTechnicians = showTechnicianFields ? formData.technicians.filter((row) => row.firstName.trim() || row.lastName.trim() || row.duty) : [];

        const requiredFields = ['vehicleModel', 'tenderRequestType', 'trainingLanguage'];
        const missingRequired = requiredFields.some((field) => checkFieldInvalid(field));
        const otherInvalid = checkFieldInvalid('numberOfTechnician') || checkFieldInvalid('numberOfDrivers') || checkFieldInvalid('preferTrainingDate') || checkFieldInvalid('alternativeDate');

        if (missingRequired || otherInvalid) {
            toast.current?.show({ severity: 'error', summary: th('tenderRequest.missingField'), detail: th('tenderRequest.formValidationMessage'), life: 4000 });
            return;
        }

        // Bkz. docs/LMS-FE-API-Dokumani-Ihale-Date-Tercuman.txt SS3.3 - sayi ve liste birebir esit olmali.
        const technicianCountMismatch =
            showTechnicianFields &&
            !((formData.numberOfTechnician == null || formData.numberOfTechnician === '') && nonEmptyTechnicians.length === 0) &&
            Number(formData.numberOfTechnician || 0) !== nonEmptyTechnicians.length;

        if (technicianCountMismatch) {
            toast.current?.show({ severity: 'error', summary: th('tenderRequest.missingField'), detail: th('tenderRequest.technicianCountMismatch'), life: 4000 });
            return;
        }

        const payload = {
            vehicleModel: formData.vehicleModel,
            tenderRequestType: Number(formData.tenderRequestType),
            // Talep tipine uymayan alanlar (bkz. showDriverCount/showTechnicianFields) her ihtimale
            // karşı burada da null'lanıyor — ekrandaki state zaten dropdown değişiminde temizleniyor.
            numberOfTechnician: showTechnicianFields && formData.numberOfTechnician != null && formData.numberOfTechnician !== '' ? Number(formData.numberOfTechnician) : null,
            numberOfDrivers: showDriverCount && formData.numberOfDrivers != null && formData.numberOfDrivers !== '' ? Number(formData.numberOfDrivers) : null,
            trainingDayCount: formData.trainingDayCount != null && formData.trainingDayCount !== '' ? Number(formData.trainingDayCount) : null,
            preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
            alternativeDate: toRequestDateTime(formData.alternativeDate),
            trainingLanguage: formData.trainingLanguage.trim(),
            description: (formData.description || '').trim(),
            translationSupport: Number(formData.translationSupport) === 1 ? 1 : 2,
            translatorName: Number(formData.translationSupport) === 1 ? (formData.translatorName || '').trim() : '',
            translatorEmail: Number(formData.translationSupport) === 1 ? (formData.translatorEmail || '').trim() : '',
            translatorPhone: Number(formData.translationSupport) === 1 ? (formData.translatorPhone || '').trim() : '',
            // Lokasyon artık tek seçim — API yine de dizi bekliyor, tek elemanlı gönderiliyor.
            locations:
                formData.location && formData.location.trainingLocation
                    ? [
                          {
                              ...(formData.location.id ? { id: formData.location.id } : {}),
                              trainingLocation: formData.location.trainingLocation,
                              fullAddress: formData.location.trainingLocation === CUSTOM_ADDRESS_LOCATION_CODE ? (formData.location.fullAddress || '').trim() || null : null
                          }
                      ]
                    : [],
            responsibles: nonEmptyResponsibles.map((row) => ({
                ...(row.id ? { id: row.id } : {}),
                firstName: row.firstName.trim(),
                lastName: row.lastName.trim(),
                phoneNumber: row.phoneNumber.trim(),
                eMail: row.eMail.trim()
            })),
            technicians: nonEmptyTechnicians.map((row) => ({
                ...(row.id ? { id: row.id } : {}),
                firstName: row.firstName.trim(),
                lastName: row.lastName.trim(),
                duty: row.duty
            }))
        };

        if (isEdit) {
            payload.id = formData.id;
        }

        onSubmit(payload);
    };

    const renderFooter = () => (
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full">
            <div>{canDeleteRecord && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}</div>
            <div className="flex align-items-center gap-2">
                {isEdit && onStatusAction && allowedTenderActions.length > 0 && (
                    <ButtonGroup
                        buttons={allowedTenderActions.map((action) => {
                            const override = TENDER_ACTION_BUTTON_OVERRIDES[action.targetStatus] || {};
                            return {
                                label: th(`tenderRequest.actions.${action.i18nKey}`),
                                icon: override.icon || action.icon,
                                severity: override.severity || 'success',
                                disabled: loading,
                                onClick: () => onStatusAction(sourceData, action.targetStatus)
                            };
                        })}
                    />
                )}
                <Button label={canEditRecord ? th('common.cancel') : th('common.close')} icon="pi pi-times" onClick={onHide} outlined type="button" />
                {canEditRecord && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
            </div>
        </div>
    );

    const isFormReady = !isEdit || !!sourceData;
    const showTranslatorName = Number(formData.translationSupport) === 1;

    return (
        <Dialog
            visible={visible}
            footer={renderFooter()}
            style={{ width: '75vw', maxWidth: '1100px' }}
            header={isEdit ? th('tenderRequest.editTitle') : th('tenderRequest.addTitle')}
            modal
            className="p-fluid"
            onHide={onHide}
            key={formData.id ?? 'new'}
        >
            <Toast ref={toast} />
            {!isFormReady || isLoadingDetail ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="p-fluid">
                    {isEdit && !canEditRecord && (
                        <div className="surface-100 border-round p-3 mb-3 text-sm">
                            <i className="pi pi-info-circle mr-2" />
                            {th('tenderRequest.viewOnlyHint')}
                        </div>
                    )}

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="vehicleModel">
                                    {th('tenderRequest.fields.vehicleModel')} <span className="text-red-500">*</span>
                                </label>
                                <LmsCodeDropdown
                                    id="vehicleModel"
                                    groupCode="VEHICLE_MODEL"
                                    value={formData.vehicleModel}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, vehicleModel: value }))}
                                    placeholder={th('tenderRequest.fields.selectPlaceholder')}
                                    invalid={isFieldInvalid('vehicleModel')}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="tenderRequestType">
                                    {th('tenderRequest.fields.tenderRequestType')} <span className="text-red-500">*</span>
                                </label>
                                <LmsCodeDropdown
                                    id="tenderRequestType"
                                    groupCode="TENDER_REQUEST_TYPE"
                                    value={formData.tenderRequestType != null ? String(formData.tenderRequestType) : null}
                                    onChange={(value) => {
                                        const numValue = value != null ? Number(value) : null;
                                        setFormData((prev) => {
                                            const next = { ...prev, tenderRequestType: numValue };
                                            const nextShowDrivers = numValue === 1 || numValue === 3;
                                            const nextShowTechnicians = numValue === 2 || numValue === 3;
                                            if (!nextShowDrivers) next.numberOfDrivers = null;
                                            if (!nextShowTechnicians) {
                                                next.numberOfTechnician = null;
                                                next.technicians = [];
                                            }
                                            return next;
                                        });
                                    }}
                                    placeholder={th('tenderRequest.fields.selectPlaceholder')}
                                    invalid={isFieldInvalid('tenderRequestType')}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        {showTechnicianFields && (
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="numberOfTechnician">{th('tenderRequest.fields.numberOfTechnician')}</label>
                                    <InputNumber
                                        id="numberOfTechnician"
                                        value={formData.numberOfTechnician}
                                        min={1}
                                        onValueChange={(e) => setFormData((prev) => ({ ...prev, numberOfTechnician: e.value }))}
                                        className={isFieldInvalid('numberOfTechnician') ? 'p-invalid' : ''}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>
                        )}

                        {showDriverCount && (
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="numberOfDrivers">{th('tenderRequest.fields.numberOfDrivers')}</label>
                                    <InputNumber
                                        id="numberOfDrivers"
                                        value={formData.numberOfDrivers}
                                        min={1}
                                        onValueChange={(e) => setFormData((prev) => ({ ...prev, numberOfDrivers: e.value }))}
                                        className={isFieldInvalid('numberOfDrivers') ? 'p-invalid' : ''}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="trainingLanguage">
                                    {th('tenderRequest.fields.trainingLanguage')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="trainingLanguage"
                                    value={formData.trainingLanguage || null}
                                    options={languageOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, trainingLanguage: e.value }))}
                                    placeholder={th('tenderRequest.fields.selectPlaceholder')}
                                    loading={isLoadingLanguages}
                                    filter
                                    showClear
                                    className={isFieldInvalid('trainingLanguage') ? 'p-invalid' : ''}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="trainingDayCount">{th('tenderRequest.fields.trainingDayCount')}</label>
                                <InputNumber
                                    id="trainingDayCount"
                                    value={formData.trainingDayCount}
                                    min={1}
                                    onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingDayCount: e.value }))}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="preferTrainingDate">{th('tenderRequest.fields.preferTrainingDate')}</label>
                                <Calendar
                                    id="preferTrainingDate"
                                    value={formData.preferTrainingDate}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, preferTrainingDate: e.value }))}
                                    dateFormat="dd.mm.yy"
                                    minDate={minTenderTrainingDate()}
                                    showIcon
                                    showClear
                                    className={isFieldInvalid('preferTrainingDate') ? 'p-invalid' : ''}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="alternativeDate">{th('tenderRequest.fields.alternativeDate')}</label>
                                <Calendar
                                    id="alternativeDate"
                                    value={formData.alternativeDate}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, alternativeDate: e.value }))}
                                    dateFormat="dd.mm.yy"
                                    minDate={minTenderTrainingDate()}
                                    showIcon
                                    showClear
                                    className={isFieldInvalid('alternativeDate') ? 'p-invalid' : ''}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <small className="text-color-secondary">{th('tenderRequest.fields.dateMinNotice')}</small>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('tenderRequest.fields.trainingEndDate')}</label>
                                <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.trainingEndDate)} disabled readOnly />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('tenderRequest.fields.alternativeEndDate')}</label>
                                <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.alternativeEndDate)} disabled readOnly />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="description">{th('tenderRequest.fields.description')}</label>
                                <InputTextarea
                                    id="description"
                                    value={formData.description}
                                    rows={3}
                                    autoResize
                                    maxLength={500}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                    placeholder={th('tenderRequest.fields.descriptionPlaceholder')}
                                    disabled={!canEditRecord}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field-checkbox mt-4">
                                <Checkbox
                                    inputId="translationSupport"
                                    checked={Number(formData.translationSupport) === 1}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, translationSupport: e.checked ? 1 : 2, translatorName: e.checked ? prev.translatorName : '', translatorEmail: e.checked ? prev.translatorEmail : '', translatorPhone: e.checked ? prev.translatorPhone : '' }))}
                                    disabled={!canEditRecord}
                                />
                                <label htmlFor="translationSupport" className="ml-2 cursor-pointer">
                                    {th('tenderRequest.fields.translationSupport')}
                                </label>
                            </div>
                        </div>

                        {showTranslatorName && (
                            <>
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="translatorName">{th('tenderRequest.fields.translatorName')}</label>
                                        <InputText
                                            id="translatorName"
                                            value={formData.translatorName}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, translatorName: e.target.value }))}
                                            placeholder={th('tenderRequest.fields.translatorNamePlaceholder')}
                                            disabled={!canEditRecord}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="translatorEmail">{th('tenderRequest.fields.translatorEmail')}</label>
                                        <InputText
                                            id="translatorEmail"
                                            value={formData.translatorEmail}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, translatorEmail: e.target.value }))}
                                            placeholder={th('tenderRequest.fields.translatorEmailPlaceholder')}
                                            disabled={!canEditRecord}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="translatorPhone">{th('tenderRequest.fields.translatorPhone')}</label>
                                        <InputText
                                            id="translatorPhone"
                                            value={formData.translatorPhone}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, translatorPhone: e.target.value }))}
                                            placeholder={th('tenderRequest.fields.translatorPhonePlaceholder')}
                                            disabled={!canEditRecord}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="surface-50 border-round p-3 mt-2">
                        <div className="flex align-items-center mb-2">
                            <h3 className="m-0" style={{ fontSize: '1rem' }}>
                                {th('tenderRequest.fields.locations')}
                            </h3>
                        </div>
                        {/* Lokasyon artık tek seçim (bkz. bu dosyanın üstündeki not) — Ekle/Sil satır
                            desenleri kaldırıldı, tek Dropdown + koşullu Açık Adres alanı. */}
                        <div className="grid align-items-start">
                            <div className="col-12 md:col-5">
                                <label>{th('tenderRequest.fields.locations')}</label>
                                <LmsCodeDropdown
                                    groupCode="TRAINING_LOCATION"
                                    value={formData.location.trainingLocation}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, location: { ...prev.location, trainingLocation: value, fullAddress: value === CUSTOM_ADDRESS_LOCATION_CODE ? prev.location.fullAddress : '' } }))}
                                    placeholder={th('tenderRequest.fields.selectPlaceholder')}
                                    disabled={!canEditRecord}
                                />
                            </div>
                            {formData.location.trainingLocation === CUSTOM_ADDRESS_LOCATION_CODE && (
                                <div className="col-12 md:col-6">
                                    <label>{th('tenderRequest.fields.fullAddress')}</label>
                                    <InputText
                                        value={formData.location.fullAddress}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, location: { ...prev.location, fullAddress: e.target.value } }))}
                                        placeholder={th('tenderRequest.fields.fullAddressPlaceholder')}
                                        disabled={!canEditRecord}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="surface-50 border-round p-3 mt-3">
                        <div className="flex align-items-center mb-2">
                            <h3 className="m-0" style={{ fontSize: '1rem' }}>
                                {th('tenderRequest.fields.responsibles')}
                            </h3>
                            {canEditRecord && <Button type="button" label={th('tenderRequest.fields.addResponsible')} icon="pi pi-plus" text style={{ marginLeft: 'auto', flex: '0 0 auto', width: 'auto' }} onClick={() => addRow('responsibles', emptyResponsibleRow)} />}
                        </div>
                        {formData.responsibles.length === 0 && <div className="text-color-secondary text-sm">{th('tenderRequest.fields.noRows')}</div>}
                        {formData.responsibles.map((row) => (
                            <div key={row.rowKey} className="surface-0 border-round p-3 mb-2 border-1 surface-border">
                                <div className="grid align-items-start">
                                    <div className="col-12 md:col-3">
                                        <label>{th('tenderRequest.fields.firstName')}</label>
                                        <InputText value={row.firstName} onChange={(e) => updateRow('responsibles', row.rowKey, { firstName: e.target.value })} disabled={!canEditRecord} />
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <label>{th('tenderRequest.fields.lastName')}</label>
                                        <InputText value={row.lastName} onChange={(e) => updateRow('responsibles', row.rowKey, { lastName: e.target.value })} disabled={!canEditRecord} />
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <label>{th('tenderRequest.fields.phoneNumber')}</label>
                                        <InputText value={row.phoneNumber} onChange={(e) => updateRow('responsibles', row.rowKey, { phoneNumber: e.target.value })} disabled={!canEditRecord} />
                                    </div>
                                    <div className="col-12 md:col-2">
                                        <label>{th('tenderRequest.fields.email')}</label>
                                        <InputText value={row.eMail} onChange={(e) => updateRow('responsibles', row.rowKey, { eMail: e.target.value })} disabled={!canEditRecord} />
                                    </div>
                                    {canEditRecord && (
                                        <div className="col-12 md:col-1 flex align-items-end justify-content-end" style={{ minHeight: '3.5rem' }}>
                                            <Button type="button" icon="pi pi-trash" severity="danger" text onClick={() => removeRow('responsibles', row.rowKey)} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {showTechnicianFields && (
                        <div className="surface-50 border-round p-3 mt-3">
                            <div className="flex align-items-center mb-2">
                                <h3 className="m-0" style={{ fontSize: '1rem' }}>
                                    {th('tenderRequest.fields.technicians')}
                                </h3>
                                {canEditRecord && <Button type="button" label={th('tenderRequest.fields.addTechnician')} icon="pi pi-plus" text style={{ marginLeft: 'auto', flex: '0 0 auto', width: 'auto' }} onClick={() => addRow('technicians', emptyTechnicianRow)} />}
                            </div>
                            {formData.technicians.length === 0 && <div className="text-color-secondary text-sm">{th('tenderRequest.fields.noRows')}</div>}
                            {formData.technicians.map((row) => (
                                <div key={row.rowKey} className="surface-0 border-round p-3 mb-2 border-1 surface-border">
                                    <div className="grid align-items-start">
                                        <div className="col-12 md:col-4">
                                            <label>{th('tenderRequest.fields.firstName')}</label>
                                            <InputText value={row.firstName} onChange={(e) => updateRow('technicians', row.rowKey, { firstName: e.target.value })} disabled={!canEditRecord} />
                                        </div>
                                        <div className="col-12 md:col-4">
                                            <label>{th('tenderRequest.fields.lastName')}</label>
                                            <InputText value={row.lastName} onChange={(e) => updateRow('technicians', row.rowKey, { lastName: e.target.value })} disabled={!canEditRecord} />
                                        </div>
                                        <div className="col-12 md:col-3">
                                            <label>{th('tenderRequest.fields.duty')}</label>
                                            <Dropdown
                                                value={row.duty}
                                                options={dutyOptions}
                                                optionLabel="label"
                                                optionValue="value"
                                                onChange={(e) => updateRow('technicians', row.rowKey, { duty: e.value })}
                                                placeholder={th('tenderRequest.fields.selectPlaceholder')}
                                                loading={isLoadingDuties}
                                                filter
                                                showClear
                                                disabled={!canEditRecord}
                                            />
                                        </div>
                                        {canEditRecord && (
                                            <div className="col-12 md:col-1 flex align-items-end justify-content-end" style={{ minHeight: '3.5rem' }}>
                                                <Button type="button" icon="pi pi-trash" severity="danger" text onClick={() => removeRow('technicians', row.rowKey)} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </form>
            )}
        </Dialog>
    );
};

export default TenderRequestForm;
